import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LABELS = {
  "heritage-suite": "Deluxe",
  "buddhist-block": "Executive",
  "orchard-cottage": "Premium",
};

/**
 * AvailabilityPeek — a subtle "3 rooms left this weekend" nudge for the
 * booking date-picker. Auto-refreshes whenever the user picks dates.
 *
 * Props: from, to  (Date | undefined)
 */
export default function AvailabilityPeek({ from, to }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const key = useMemo(() => {
    if (from && to) return `${format(from, "yyyy-MM-dd")}_${format(to, "yyyy-MM-dd")}`;
    return "weekend";
  }, [from, to]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params =
      from && to
        ? { check_in: format(from, "yyyy-MM-dd"), check_out: format(to, "yyyy-MM-dd") }
        : {};
    axios
      .get(`${API}/bookings/availability/summary`, { params })
      .then(({ data }) => {
        if (alive) setSummary(data);
      })
      .catch(() => alive && setSummary(null))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading && !summary) {
    return (
      <div
        data-testid="availability-peek-loading"
        className="mt-3 text-xs font-mono-label text-[#B8A891]/60"
      >
        Checking availability…
      </div>
    );
  }
  if (!summary) return null;

  const { range, remaining, inventory, total_remaining, total_inventory } = summary;

  // Sold out entirely
  if (total_remaining === 0) {
    return (
      <div
        data-testid="availability-peek-soldout"
        className="mt-3 flex items-start gap-3 border border-[#D97645]/40 bg-[#D97645]/10 px-4 py-3 text-sm"
      >
        <span className="mt-[2px] inline-block h-2 w-2 rounded-full bg-[#D97645]" />
        <div>
          <div className="font-mono-label text-[#D97645]">
            {range.label} · Fully booked
          </div>
          <div className="mt-1 text-[#F2E7D0]/80 text-xs">
            No rooms available for {range.check_in} → {range.check_out}. Try a different date.
          </div>
        </div>
      </div>
    );
  }

  // Find the tightest category to build the headline urgency copy
  const lowestEntry = Object.entries(remaining)
    .filter(([, n]) => n > 0)
    .sort((a, b) => a[1] - b[1])[0];
  const lowestName = lowestEntry ? LABELS[lowestEntry[0]] : "";
  const lowestCount = lowestEntry ? lowestEntry[1] : 0;

  const urgent = total_remaining <= 3 || lowestCount <= 2;
  const dot = urgent ? "bg-[#D97645]" : "bg-[#F2E7D0]/60";
  const border = urgent ? "border-[#D97645]/40" : "border-white/10";

  const headline = urgent
    ? total_remaining <= 3
      ? `Only ${total_remaining} of ${total_inventory} rooms left · ${range.label}`
      : `Only ${lowestCount} ${lowestName} ${lowestCount === 1 ? "suite" : "suites"} left · ${range.label}`
    : `${total_remaining} of ${total_inventory} rooms available · ${range.label}`;

  return (
    <div
      data-testid="availability-peek"
      data-total-remaining={total_remaining}
      className={`mt-3 flex flex-col sm:flex-row sm:items-center gap-3 border ${border} bg-[#27221C] px-4 py-3`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className={`inline-block h-2 w-2 rounded-full ${dot}`} />
        <div className="font-mono-label text-[#F2E7D0] truncate">{headline}</div>
      </div>
      <div className="sm:ml-auto flex flex-wrap gap-2">
        {Object.entries(inventory).map(([rt, inv]) => {
          const left = remaining[rt] ?? 0;
          const soldOut = left === 0;
          const low = left > 0 && left <= 1;
          const color = soldOut
            ? "border-white/10 text-[#B8A891]/50 line-through"
            : low
            ? "border-[#D97645]/50 text-[#D97645]"
            : "border-white/15 text-[#F2E7D0]/85";
          return (
            <span
              key={rt}
              data-testid={`availability-peek-${rt}`}
              className={`inline-flex items-center gap-2 border ${color} px-2.5 py-1 text-[10px] tracking-[0.2em] uppercase font-mono-label`}
              title={`${left} of ${inv} ${LABELS[rt]} rooms left`}
            >
              <span>{LABELS[rt] || rt}</span>
              <span className="opacity-70">
                {soldOut ? "sold out" : `${left}/${inv}`}
              </span>
            </span>
          );
        })}
      </div>
    </div>
  );
}
